import { useNavigate } from "@entry/navigation";
import styled from "styled-components";

import { Email, Google, Phone } from "@assets";
import { AnalyticsEvent, Routes } from "@ctypes";
import { entryColors, entryFont, entryLayout } from "@entry/tokens";
import { EntryBody, EntryCta, EntryTitle } from "@entry/ui";
import { useEntryAnimation } from "@entry/useEntryAnimation";
import { Translations, i18n } from "@i18n";
import { Analytics, Authentication } from "@services";
import { Screen, ScreenColumn, ScreenCta, useCtaSpacer, useEntryPageAnalytics } from "./shared";

const PhoneIconSlot = styled.span`
  display: flex;
  align-items: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Divider = styled.div`
  width: 100%;
  max-width: ${entryLayout.ctaWidth}px;
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
  margin: 4px 0;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: ${entryLayout.ctaWidth}px;
`;

const SocialPill = styled.button`
  flex: 1;
  min-width: 0;
  height: ${entryLayout.ctaHeightPx}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${entryLayout.radiusPill}px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  cursor: pointer;
  transition:
    background 160ms ease,
    transform 160ms ease;

  svg {
    width: 22px;
    height: 22px;
    fill: ${entryColors.textPrimary};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0) scale(0.985);
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 3px;
  }
`;

const SipcDisclaimer = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 11px;
  line-height: 14px;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
  max-width: ${entryLayout.ctaWidth}px;
`;

export const LoginOptionsScreen = () => {
  const navigate = useNavigate();

  useEntryPageAnalytics(Routes.Login);

  const { contentStyle, ctaStyle } = useEntryAnimation();
  const { ctaRef, ctaSpacerHeight } = useCtaSpacer();

  const handlePhoneLogin = () => {
    Analytics.track(AnalyticsEvent.AuthRoutePhoneCTAPressed);
    navigate(Routes.PhoneLoginInput);
  };

  const handleGoogleLogin = () => {
    Analytics.track(AnalyticsEvent.AuthRouteGoogleCTAPressed);
    Authentication.loginWithGoogle();
  };

  const handleEmailLogin = () => {
    Analytics.track(AnalyticsEvent.AuthRouteEmailCTAPressed);
    navigate(Routes.EmailLogin);
  };

  return (
    <Screen>
      <ScreenColumn>
        <div style={contentStyle}>
          <EntryTitle $align="center" $size={40}>
            {i18n.t(Translations.EntryLoginTitle)}
          </EntryTitle>
          <EntryBody $align="center" style={{ marginTop: 16 }}>
            {i18n.t(Translations.EntryLoginSubtitle)}
          </EntryBody>
        </div>

        <div aria-hidden="true" style={{ height: ctaSpacerHeight, flexShrink: 0 }} />
        <ScreenCta ref={ctaRef} style={ctaStyle}>
          <SipcDisclaimer>
            {i18n.t(Translations.EntryLoginSipcDisclaimer)}
          </SipcDisclaimer>

          <EntryCta type="button" $variant="terracotta" onClick={handlePhoneLogin}>
            <PhoneIconSlot aria-hidden="true">
              <Phone />
            </PhoneIconSlot>
            {i18n.t(Translations.EntryLoginPhoneCTA)}
          </EntryCta>

          <Divider />

          <SocialRow>
            <SocialPill
              type="button"
              aria-label={i18n.t(Translations.EntryLoginGoogleCTA)}
              onClick={handleGoogleLogin}
            >
              <Google />
            </SocialPill>
            <SocialPill
              type="button"
              aria-label={i18n.t(Translations.EntryLoginEmailCTA)}
              onClick={handleEmailLogin}
            >
              <Email />
            </SocialPill>
          </SocialRow>
        </ScreenCta>
      </ScreenColumn>
    </Screen>
  );
};
